import styled from "@emotion/styled";
import {
  AllCaps,
  Avatar,
  Box,
  CtaBoxIcon,
  FlexRow,
  H2,
  HeadingGroup,
  Link,
  List,
  Stack,
  Text,
  IconChatHeart,
  H3,
} from "atoms";
import { ReviewCard } from "features/reviews";
import { formatDate } from "utils/date";
import { pluralize } from "utils/text";
import { HomePageDataShape } from "../api/useHomePageData";

type HomeReviewsProps = {
  newestReviews: HomePageDataShape["newestReviews"];
  topReviewersForMonth: HomePageDataShape["topReviewersForMonth"];
};

const today = new Date().toISOString().slice(0, 10);

function HomeReviews({
  newestReviews,
  topReviewersForMonth,
}: HomeReviewsProps) {
  return (
    <Wrapper>
      <Stack gap="md">
        <HeadingGroup>
          <small>What They&apos;re Saying</small>
          <H2>Recent Reviews</H2>
        </HeadingGroup>
        <div>
          <ReviewList type="blank">
            {newestReviews.map(({ review }) => (
              <ReviewCard key={review.reviewId} review={review} />
            ))}
          </ReviewList>
        </div>
      </Stack>

      <TopReviewersStack gap="md">
        <HeadingGroup>
          <small>{formatDate(today, { month: "long" })} Leaderboard</small>
          <H2>Top Reviewers</H2>
        </HeadingGroup>

        <TopReviewersBox>
          {!topReviewersForMonth?.length ? (
            <FlexRow flexDirection="column" gap="0" alignItems="stretch">
              <FlexRow justifyContent="flex-end">
                <CtaBoxIcon>
                  <IconChatHeart accent="accent1" />
                </CtaBoxIcon>
              </FlexRow>

              <H3 size="md">Reviewers Coming&nbsp;Soon</H3>

              <NoReviewersBody size="xs">
                Each month, <AllCaps>Hug</AllCaps> features our top reviewers on
                the homepage. Get a head start on our leaderboard by being the
                first to leave a&nbsp;review!
              </NoReviewersBody>

              <NoReviewersLink
                to="/search/?type=artist&sort=reviewCount"
                variant="secondary"
                size="xs"
                arrow="r"
              >
                Find an Artist to Review
              </NoReviewersLink>
            </FlexRow>
          ) : (
            <List type="blank">
              {topReviewersForMonth.map((reviewer) => (
                <Reviewer
                  as="li"
                  gap=".66rem"
                  flexWrap="nowrap"
                  itemsFlex="0 1 auto"
                  key={reviewer.name}
                >
                  <SAvatar src={reviewer.avatarURL} size={50} />

                  <Stack gap="xxs">
                    <H3 size="sm">
                      <Link to={`/users/${reviewer.id}`}>{reviewer.name}</Link>
                    </H3>
                    <Text size="xxxs">
                      {pluralize(reviewer.count, "Review")}
                    </Text>
                  </Stack>
                </Reviewer>
              ))}
            </List>
          )}
        </TopReviewersBox>
      </TopReviewersStack>
    </Wrapper>
  );
}

const Wrapper = styled.section(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "4.5rem",
  [theme.breakpoints.desktop]: {
    gap: theme.spacing.gap,
    gridTemplateColumns: "2fr 1fr",
  },
}));

const ReviewList = styled(List)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing.gap,
  [theme.breakpoints.desktop]: {
    gridTemplateColumns: "1fr 1fr",
  },
}));

const TopReviewersBox = styled(Box)({
  display: "flex",
  flexdirection: "column",
});

const TopReviewersStack = styled(Stack)({
  display: "flex",
  flexDirection: "column",
});

const Reviewer = styled(FlexRow)(({ theme }) => ({
  padding: "1rem 0",
  "&+&": {
    borderTop: `1px solid ${theme.colors.fg20}`,
  },
  "&:first-of-type": {
    paddingTop: 0,
  },
  "&:last-of-type": {
    paddingBottom: 0,
  },
  p: {
    lineHeight: 1.1,
  },
  "p+p": {
    color: theme.colors.fg60,
    fontWeight: "bold",
  },
  span: {
    color: theme.colors.fg60,
  },
}));

const SAvatar = styled(Avatar)({
  alignSelf: "start",
});
const NoReviewersBody = styled(Text)({
  flex: 1,
});

const NoReviewersLink = styled(Link)({
  alignSelf: "start",
});

export { HomeReviews };
